<template>
  <div>
    <!-- Filtros -->
    <report-filters
      :selectedLegalEntity.sync="selectedLegalEntity"
      :startDate.sync="startDate"
      :endDate.sync="endDate"
      :loading="loading"
      :hasData="reportData && reportData.length > 0"
      reportType="response_time"
      @search="loadReportData"
      @clear="clearFilters"
      @export="exportToPdf"
    >
      <template #additional-filters>
        <b-row class="mt-2">
          <b-col cols="12" md="3">
            <b-form-group
              :label="
                $t(
                  'reporting_channel.reports.response_time.filters.protocol_number'
                )
              "
            >
              <b-form-input
                v-model="protocolNumber"
                :placeholder="
                  $t(
                    'reporting_channel.reports.response_time.filters.protocol_number_placeholder'
                  )
                "
                type="text"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </template>
    </report-filters>

    <!-- Tabela de Resultados -->
    <b-row>
      <b-col cols="12">
        <b-card>
          <b-card-title>{{
            $t('reporting_channel.reports.response_time.history_title')
          }}</b-card-title>
          <b-card-body>
            <b-alert
              v-if="!reportData || reportData.length === 0"
              show
              variant="info"
            >
              {{
                $t('reporting_channel.reports.response_time.no_data_message')
              }}
            </b-alert>
            <div v-else>
              <!-- Lista agrupada por Handling/Form -->
              <div
                v-for="handling in reportData"
                :key="handling.handling_id"
                class="mb-4"
              >
                <!-- Cabeçalho do Handling -->
                <b-card class="mb-2 border-primary">
                  <b-card-body>
                    <b-row class="align-items-center">
                      <b-col cols="12" md="6">
                        <div class="d-flex align-items-center">
                          <feather-icon
                            icon="FileTextIcon"
                            size="20"
                            class="mr-2 text-primary"
                          />
                          <div>
                            <h5 class="mb-0">
                              <strong class="text-primary"
                                >{{
                                  $t(
                                    'reporting_channel.reports.response_time.protocol'
                                  )
                                }}:
                                {{ handling.protocol_number || '-' }}</strong
                              >
                            </h5>
                            <small class="text-muted"
                              >{{
                                $t(
                                  'reporting_channel.reports.response_time.handling_id'
                                )
                              }}: {{ handling.handling_id }}</small
                            >
                          </div>
                        </div>
                      </b-col>
                      <b-col cols="12" md="6" class="mt-2 mt-md-0">
                        <div v-if="handling.legal_entity">
                          <div class="d-flex align-items-center">
                            <feather-icon
                              icon="BuildingIcon"
                              size="18"
                              class="mr-2 text-secondary"
                            />
                            <div>
                              <strong class="text-dark">{{
                                handling.legal_entity.company_name || '-'
                              }}</strong>
                              <div
                                v-if="handling.legal_entity.trading_name"
                                class="text-muted small"
                              >
                                {{ handling.legal_entity.trading_name }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-muted">
                          <small>{{
                            $t(
                              'reporting_channel.reports.response_time.institution_not_informed'
                            )
                          }}</small>
                        </div>
                      </b-col>
                    </b-row>
                  </b-card-body>
                </b-card>

                <!-- Tabela do Histórico -->
                <b-table
                  :items="handling.history"
                  :fields="tableFields"
                  striped
                  hover
                  responsive
                  small
                  class="mb-0"
                  :tbody-tr-class="'history-row'"
                >
                  <template #cell(status_str)="row">
                    <HandlingStatusBadge
                      :label="row.item.status_str || '-'"
                      :color="
                        (row.item.status_obj && row.item.status_obj.color) ||
                          row.item.status_color ||
                          null
                      "
                    />
                  </template>

                  <template #cell(created_date_formatted)="row">
                    {{
                      row.item.created_date_formatted ||
                        (row.item.created_at
                          ? this.$utils.format.date(row.item.created_at, false)
                          : '-')
                    }}
                  </template>

                  <template #cell(time_in_status_formatted)="{ item, value }">
                    <div v-if="item.status === 'CL'">
                      <div
                        v-if="
                          handling.total_time_in_status_formatted &&
                            handling.total_time_in_status_formatted !==
                              'Menos de 1 hora'
                        "
                        class="text-primary"
                      >
                        <strong>Tempo total:</strong>
                        {{ handling.total_time_in_status_formatted }}
                      </div>
                    </div>
                    <div v-else>
                      <strong class="text-primary">
                        {{ value || '-' }}
                      </strong>
                    </div>
                  </template>

                  <template #cell(user)="row">
                    {{
                      row.item.user && row.item.user.name
                        ? row.item.user.name
                        : '-'
                    }}
                  </template>
                </b-table>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardSubTitle,
  BCardBody,
  BRow,
  BCol,
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BTable,
  BSpinner,
  BBadge
} from 'bootstrap-vue'
import ReportFilters from '@/components/report/ReportFilters.vue'
import ReportingChannelFormHandlingService from '@/services/api/reporting-channel/ReportingChannelFormHandlingService'
import HandlingStatusBadge from '@/components/HandlingStatusBadge.vue'
import { isAdmin } from '@/util/Util'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BAlert,
    BButton,
    BFormGroup,
    BFormInput,
    BTable,
    BSpinner,
    BBadge,
    ReportFilters,
    HandlingStatusBadge
  },
  data() {
    // Calcular datas dos últimos 30 dias
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 30)

    return {
      service: null,
      selectedLegalEntity: null,
      protocolNumber: null,
      startDate: startDate,
      endDate: endDate,
      reportData: null,
      loading: false
    }
  },
  computed: {
    tableFields() {
      return [
        {
          key: 'status_str',
          label: this.$t(
            'reporting_channel.reports.response_time.table.status'
          ),
          sortable: true
        },
        {
          key: 'created_date_formatted',
          label: this.$t(
            'reporting_channel.reports.response_time.table.treatment_date'
          ),
          sortable: true
        },
        {
          key: 'time_in_status_formatted',
          label: this.$t(
            'reporting_channel.reports.response_time.table.time_in_status'
          ),
          sortable: true
        },
        {
          key: 'user',
          label: this.$t('reporting_channel.reports.response_time.table.user'),
          sortable: false
        }
      ]
    }
  },
  methods: {
    getStatusBadgeVariant(item) {
      if (!item) return 'secondary'

      // Se vier o objeto status completo com cor, usar a cor do enum
      if (item.status_obj && item.status_obj.color) {
        // Mapear 'grey' para 'secondary' (padrão do BootstrapVue)
        const color =
          item.status_obj.color === 'grey' ? 'secondary' : item.status_obj.color
        return color
      }

      // Fallback para o status string se não vier o objeto
      if (item.status) {
        const statusMap = {
          OP: 'primary',
          IV: 'warning',
          IH: 'warning',
          PE: 'warning',
          VA: 'success',
          CL: 'secondary' // CLOSED usa 'grey' no enum, que mapeia para 'secondary'
        }
        return statusMap[item.status] || 'secondary'
      }

      return 'secondary'
    },
    loadReportData() {
      this.loading = true
      this.$vs.loading()

      const params = {}
      if (this.selectedLegalEntity && this.selectedLegalEntity.id) {
        params.legal_entity_id = this.selectedLegalEntity.id
      }
      if (this.protocolNumber && this.protocolNumber.trim()) {
        params.protocol_number = this.protocolNumber.trim()
      }
      if (this.startDate) {
        params.start_date = this.formatDateForApi(this.startDate)
      }
      if (this.endDate) {
        params.end_date = this.formatDateForApi(this.endDate)
      }

      this.service
        .getResponseTimeReport(params)
        .then(
          data => {
            this.reportData = data
            this.$vs.loading.close()
            this.loading = false
          },
          error => {
            this.$vs.loading.close()
            this.loading = false
            this.notifyError(
              this.$vs,
              'Não foi possível carregar os dados do relatório'
            )
          }
        )
        .finally(() => {
          this.$vs.loading.close()
          this.loading = false
        })
    },
    formatDateForApi(date) {
      if (!date) return null
      if (typeof date === 'string') {
        // Se já estiver no formato YYYY-MM-DD, retornar como está
        if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return date
        }
        // Se estiver no formato DD/MM/YYYY, converter
        const parts = date.split('/')
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`
        }
      }
      // Se for um objeto Date
      if (date instanceof Date) {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      return null
    },
    clearFilters() {
      this.selectedLegalEntity = null
      this.protocolNumber = null
      // Resetar para os últimos 30 dias
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30)
      this.startDate = startDate
      this.endDate = endDate
      this.reportData = null
    },
    exportToPdf() {
      if (!this.reportData || this.reportData.length === 0) {
        this.$vs.notify({
          title: this.$t('messages.error'),
          text: 'Não há dados para exportar',
          color: 'warning',
          icon: 'AlertCircleIcon'
        })
        return
      }

      this.$vs.loading()

      // Preparar parâmetros de filtro para o PDF
      const params = {}
      if (this.selectedLegalEntity && this.selectedLegalEntity.id) {
        params.legal_entity_id = this.selectedLegalEntity.id
      }
      if (this.protocolNumber && this.protocolNumber.trim()) {
        params.protocol_number = this.protocolNumber.trim()
      }
      if (this.startDate) {
        params.start_date = this.formatDateForApi(this.startDate)
      }
      if (this.endDate) {
        params.end_date = this.formatDateForApi(this.endDate)
      }

      // Chamar endpoint de exportação PDF
      this.service
        .exportResponseTimeReportPdf(params)
        .then(() => {
          this.$vs.loading.close()
          this.$vs.notify({
            title: this.$t('messages.success_title'),
            text: 'Relatório PDF gerado com sucesso',
            color: 'success',
            icon: 'CheckIcon'
          })
        })
        .catch(error => {
          this.$vs.loading.close()
          this.notifyError(this.$vs, 'Não foi possível gerar o relatório PDF')
        })
    }
  },
  beforeMount() {
    this.service = ReportingChannelFormHandlingService.build(this.$vs)
  },
  mounted() {
    // Carregar dados automaticamente ao montar o componente
    this.loadReportData()
  }
}
</script>

<style scoped>
/* Estilos específicos do relatório */
.history-row {
  background-color: #f8f9fa;
}

.history-row:hover {
  background-color: #e9ecef;
}
</style>
